import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  InputNumber,
  App,
  Typography,
  Spin,
  Image,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const PropertyForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [propertyTypes, setPropertyTypes] = useState<any[]>([]);
  const [propertyAreas, setPropertyAreas] = useState<any[]>([]); // 1. State baru
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { message } = App.useApp();

  const fetchProperty = useCallback(async () => {
    setFormLoading(true);
    try {
      const response = await api.get(`properties/${id}`);
      const property = response.data.data;
      
      let images = [];
      if (property.images) {
        try {
          images = JSON.parse(property.images);
          setImageUrls(images);
        } catch (e) {
          console.error('Failed to parse images:', e);
        }
      }

      form.setFieldsValue({
        ...property,
        images: images,
      });
    } catch (error: any) {
      console.error('Failed to fetch property:', error);
      message.error('Gagal memuat data properti');
      navigate('/properties');
    } finally {
      setFormLoading(false);
    }
  }, [id, form, navigate, message]);

  useEffect(() => {
    const fetchPropertyTypes = async () => {
      try {
        const response = await api.get('property-types');
        setPropertyTypes(response.data.data);
      } catch (error: any) {
        console.error('Failed to fetch property types:', error);
        message.error('Gagal memuat tipe properti');
      }
    };

    // 2. Fungsi untuk fetch area properti
    const fetchPropertyAreas = async () => {
      try {
        const response = await api.get('property-areas', { params: { limit: 1000 } });
        setPropertyAreas(response.data.data || []);
      } catch (error: any) {
        console.error('Failed to fetch property areas:', error);
        message.error('Gagal memuat area properti');
      }
    };

    fetchPropertyTypes();
    fetchPropertyAreas(); // Panggil fungsi fetch
    if (isEdit) {
      fetchProperty();
    }
  }, [isEdit, fetchProperty, message]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const submitData = {
        ...values,
        images: JSON.stringify(imageUrls),
      };

      if (isEdit) {
        await api.put(`properties/${id}`, submitData);
        message.success('Properti berhasil diperbarui');
      } else {
        await api.post('properties', submitData);
        message.success('Properti berhasil dibuat');
      }

      navigate('/properties');
    } catch (error: any) {
      console.error('Failed to save property:', error);
      message.error(error.response?.data?.message || 'Gagal menyimpan properti');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (!value) return '';
    return new Intl.NumberFormat('id-ID').format(value);
  };

  const parseCurrency = (value: string | undefined) => {
    return value ? parseFloat(value.replace(/\D/g, '')) : 0;
  };

  const handleImageAdd = () => {
    const newImageUrl = `https://picsum.photos/400/300?random=${Date.now()}`;
    setImageUrls([...imageUrls, newImageUrl]);
  };

  const handleImageRemove = (index: number) => {
    const newImages = imageUrls.filter((_, i) => i !== index);
    setImageUrls(newImages);
  };

  if (formLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            {isEdit ? 'Edit Properti' : 'Tambah Properti'}
          </Title>
        </Col>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/properties')}
          >
            Kembali
          </Button>
        </Col>
      </Row>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            status: 'available',
            bedrooms: 0,
            bathrooms: 0,
            garage: 0,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Title level={4}>Informasi Dasar</Title>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Judul Properti"
                name="title"
                rules={[{ required: true, message: 'Judul properti wajib diisi' }]}
              >
                <Input placeholder="Masukkan judul properti" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Tipe Properti"
                name="property_type_id"
                rules={[{ required: true, message: 'Tipe properti wajib dipilih' }]}
              >
                <Select placeholder="Pilih tipe properti">
                  {propertyTypes.map((type: any) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Area Properti"
                name="property_area_id"
                rules={[{ required: true, message: 'Area properti wajib dipilih' }]}
              >
                <Select placeholder="Pilih area properti">
                  {propertyAreas.map((area: any) => (
                    <Option key={area.id} value={area.id}>
                      {area.Name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Deskripsi" name="description">
                <TextArea
                  rows={4}
                  placeholder="Masukkan deskripsi properti"
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Title level={4} style={{ marginTop: 24 }}>
                Harga & Lokasi
              </Title>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Harga"
                name="price"
                rules={[{ required: true, message: 'Harga wajib diisi' }]}
              >
                <InputNumber
                  placeholder="Masukkan harga"
                  style={{ width: '100%' }}
                  formatter={formatCurrency}
                  parser={parseCurrency}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Status" name="status">
                <Select>
                  <Option value="available">Available</Option>
                  <Option value="sold">Sold</Option>
                  <Option value="reserved">Reserved</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Alamat"
                name="address"
                rules={[{ required: true, message: 'Alamat wajib diisi' }]}
              >
                <Input placeholder="Masukkan alamat lengkap" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Kota"
                name="city"
                rules={[{ required: true, message: 'Kota wajib diisi' }]}
              >
                <Input placeholder="Masukkan kota" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Provinsi" name="province">
                <Input placeholder="Masukkan provinsi" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Kode Pos" name="postal_code">
                <Input placeholder="Masukkan kode pos" />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Title level={4} style={{ marginTop: 24 }}>
                Spesifikasi
              </Title>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Luas Tanah (m²)" name="land_size">
                <InputNumber
                  placeholder="Masukkan luas tanah"
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Luas Bangunan (m²)" name="building_size">
                <InputNumber
                  placeholder="Masukkan luas bangunan"
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Kamar Tidur" name="bedrooms">
                <InputNumber
                  placeholder="Jumlah kamar tidur"
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Kamar Mandi" name="bathrooms">
                <InputNumber
                  placeholder="Jumlah kamar mandi"
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Garasi" name="garage">
                <InputNumber
                  placeholder="Jumlah garasi"
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Title level={4} style={{ marginTop: 24 }}>
                Foto Properti
              </Title>
              <Card>
                <Button
                  type="dashed"
                  icon={<UploadOutlined />}
                  onClick={handleImageAdd}
                  style={{ marginBottom: 16 }}
                >
                  Tambah Foto (Demo)
                </Button>
                <Row gutter={[16, 16]}>
                  {imageUrls.map((url, index) => (
                    <Col xs={12} sm={8} md={6} key={index}>
                      <div style={{ position: 'relative' }}>
                        <Image
                          src={url}
                          height={150}
                          style={{
                            objectFit: 'cover',
                            borderRadius: 8,
                            width: '100%',
                          }}
                        />
                        <Button
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'rgba(255, 255, 255, 0.9)',
                          }}
                          onClick={() => handleImageRemove(index)}
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>

            <Col xs={24}>
              <Form.Item style={{ marginTop: 32 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                >
                  {isEdit ? 'Update Properti' : 'Simpan Properti'}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default PropertyForm;